import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  CompanyTypeOption,
  CompanyTypeOverride,
  deleteOverride,
  listOverrides,
} from "@/services/companyClassificationApi";

import { ALL, companyTypeLabel } from "./classification";
import { OverrideDialog, OverrideDraft } from "./OverrideDialog";

export function OverridesTab({ types }: { types: CompanyTypeOption[] }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CompanyTypeOverride[]>([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<OverrideDraft | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listOverrides(navigate, { q: search.trim(), type: typeFilter === ALL ? undefined : typeFilter }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate, search, typeFilter]);

  useEffect(() => {
    const timer = setTimeout(refresh, 250);
    return () => clearTimeout(timer);
  }, [refresh]);

  const handleDelete = async (row: CompanyTypeOverride) => {
    const ok = await confirmDialog({
      title: "Supprimer la correction",
      description: `« ${row.name ?? row.linkedin_company_ids.join(", ")} » redeviendra classée par ses signaux et son industrie.`,
      confirmLabel: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteOverride(row.id, navigate);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="h-9 w-64"
          placeholder="Rechercher un nom, alias ou ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les types</SelectItem>
            {types
              .filter((type) => type.value !== "other")
              .map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="mr-1 h-3 w-3" /> Rafraîchir
        </Button>
        <Button size="sm" className="ml-auto" onClick={() => setDraft({})}>
          <Plus className="mr-1 h-3 w-3" /> Nouvelle correction
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Nom</TableHead>
            <TableHead>Alias</TableHead>
            <TableHead>IDs LinkedIn</TableHead>
            <TableHead>Note</TableHead>
            <TableHead>Modifié par</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                Aucune correction
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <Badge variant="secondary">{companyTypeLabel(row.company_type)}</Badge>
              </TableCell>
              <TableCell className="font-medium">{row.name ?? "—"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{row.aliases.join(", ") || "—"}</TableCell>
              <TableCell className="text-sm">{row.linkedin_company_ids.join(", ") || "—"}</TableCell>
              <TableCell className="max-w-64 truncate text-sm text-muted-foreground">{row.note ?? ""}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{row.updated_by ?? ""}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" aria-label="Modifier" onClick={() => setDraft(row)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" aria-label="Supprimer" onClick={() => handleDelete(row)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <OverrideDialog
        open={draft !== null}
        onOpenChange={(open) => !open && setDraft(null)}
        types={types}
        draft={draft}
        onSaved={refresh}
      />
    </div>
  );
}
